/**
 * Main Layout Component
 *
 * Application shell with sidebar navigation and header.
 * Uses the KRP Design System for consistent styling.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import NotificationBell from '../notifications/NotificationBell';
import { useNotifications } from '../../contexts/NotificationContext';
import { cn } from '../../utils/cn';
import OfflineBanner from './OfflineBanner';
import { VERSION_LABEL } from '../../version';
// P-S616-1: เมนูย้ายไป config/navigation.tsx เพื่อให้ post-login redirect + หน้า access-denied
// ใช้ลำดับและ permission ชุดเดียวกับ sidebar (source of truth เดียว ไม่ drift)
import { menuItems, resolveLandingRoute, type MenuItem } from '../../config/navigation';
import {
  RiBuildingLine,
  RiUserSettingsLine,
  RiSettingsLine,
  RiMenuLine,
  RiCloseLine,
  RiSearchLine,
  RiLogoutBoxLine,
  RiMenuFoldLine,
  RiMenuUnfoldLine,
  RiQuestionLine,
  RiArrowDownSLine,
} from 'react-icons/ri';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  const mobileSidebarScrollRef = useRef<HTMLDivElement>(null);

  // Sidebar scroll position preservation
  const SIDEBAR_SCROLL_KEY = 'krp_sidebar_scroll_position';

  // FEEDBACK-SIDEBAR-COLLAPSE (user report 2026-08-24): แต่ละหัวข้อเมนูย่อ/ขยายได้
  // เพื่อซ่อนเมนูที่ไม่ค่อยได้ใช้ · จำสถานะไว้ข้ามการเปิดแอพผ่าน localStorage
  // key = menuItem.path (unique ต่อหัวข้อ) · default = ขยาย (พฤติกรรมเดิม)
  // FEEDBACK-SIDEBAR-RAIL (user report 2026-09-07): บนโน้ตบุ๊กจอเล็ก sidebar 256px กินพื้นที่ ~20%
  // ตลอดเวลาและซ่อนไม่ได้ (เดิมมีปุ่มเปิด/ปิดเฉพาะจอ < lg) → เพิ่มโหมด "แถบไอคอน" 64px บน lg ขึ้นไป
  // จำสถานะข้ามการเปิดแอพเหมือน collapsedSections
  // FEEDBACK-MENU-SEARCH (user 2026-09-08): เมนูมี 13 กลุ่ม + 54 เมนูย่อย = 68 รายการ หายาก
  // → ช่องค้นหาที่กรองทั้งชื่อกลุ่มและเมนูย่อย (เห็นเฉพาะเมนูที่ผู้ใช้มีสิทธิ์เหมือนเดิม)
  const [menuQuery, setMenuQuery] = useState('');
  const menuSearchRef = useRef<HTMLInputElement>(null);
  const mobileMenuSearchRef = useRef<HTMLInputElement>(null);

  // เทียบแบบไม่สนตัวพิมพ์/ช่องว่าง — ผู้ใช้พิมพ์ "รายการเช่า" หรือ "รายการ เช่า" ก็เจอ
  const normalizeMenuText = (text: string) => text.toLowerCase().replace(/\s+/g, '');

  const SIDEBAR_RAIL_KEY = 'krp_sidebar_rail';
  const [isRail, setIsRail] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_RAIL_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleRail = () => {
    setIsRail(prev => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_RAIL_KEY, next ? '1' : '0');
      } catch {
        // private mode — สถานะอยู่แค่ใน memory
      }
      setRailFlyout(null);
      return next;
    });
  };

  // flyout ของเมนูที่มีเมนูย่อย ตอนอยู่โหมดแถบไอคอน — ใช้ fixed position เพราะ container เมนู
  // เป็น overflow-y-auto (panel ที่วางใน flow จะโดน clip)
  const [railFlyout, setRailFlyout] = useState<{ key: string; top: number } | null>(null);
  const railFlyoutTimer = useRef<number | null>(null);

  const openRailFlyout = (key: string, el: HTMLElement) => {
    if (railFlyoutTimer.current) {
      window.clearTimeout(railFlyoutTimer.current);
      railFlyoutTimer.current = null;
    }
    setRailFlyout({ key, top: el.getBoundingClientRect().top });
  };

  const openRailFlyoutKeepOpen = () => {
    if (railFlyoutTimer.current) {
      window.clearTimeout(railFlyoutTimer.current);
      railFlyoutTimer.current = null;
    }
  };

  // หน่วงตอนเมาส์ออก เพื่อให้ลากเมาส์จากไอคอนไปยัง flyout ได้ทัน
  const scheduleCloseRailFlyout = () => {
    if (railFlyoutTimer.current) window.clearTimeout(railFlyoutTimer.current);
    railFlyoutTimer.current = window.setTimeout(() => setRailFlyout(null), 180);
  };

  const COLLAPSED_SECTIONS_KEY = 'krp_sidebar_collapsed_sections';
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });

  const persistCollapsedSections = (next: Record<string, boolean>) => {
    try {
      localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next));
    } catch {
      // localStorage อาจใช้ไม่ได้ (private mode) — ยอมให้สถานะอยู่แค่ใน memory
    }
  };

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [sectionKey]: !prev[sectionKey] };
      persistCollapsedSections(next);
      return next;
    });
  };

  // Save scroll position when scrolling
  useEffect(() => {
    const sidebarEl = sidebarScrollRef.current;
    const mobileSidebarEl = mobileSidebarScrollRef.current;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(target.scrollTop));
    };

    if (sidebarEl) {
      sidebarEl.addEventListener('scroll', handleScroll);
    }
    if (mobileSidebarEl) {
      mobileSidebarEl.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (sidebarEl) {
        sidebarEl.removeEventListener('scroll', handleScroll);
      }
      if (mobileSidebarEl) {
        mobileSidebarEl.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Restore scroll position after navigation
  useEffect(() => {
    const savedScrollTop = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (savedScrollTop) {
      const scrollTop = parseInt(savedScrollTop, 10);
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (sidebarScrollRef.current) {
          sidebarScrollRef.current.scrollTop = scrollTop;
        }
        if (mobileSidebarScrollRef.current) {
          mobileSidebarScrollRef.current.scrollTop = scrollTop;
        }
      });
    }
  }, [location.pathname]);

  // Notification context
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    removeNotification
  } = useNotifications();

  // Click outside + Escape handler for user dropdown (BUG-A11Y-NOTI-002 same-pattern:
  // keyboard users must dismiss via Escape, not only mouse outside-click — WCAG 2.1.1)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserDropdownOpen(false);
        userButtonRef.current?.focus(); // คืน focus กลับที่ปุ่ม (WCAG 2.4.3)
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isUserDropdownOpen]);

  // Permission hook
  const { hasPermission: checkPermission, isSuperAdmin, isSuperUser } = usePermission();


  // ฟังก์ชันตรวจสอบว่า path ปัจจุบันตรงกับเมนูหรือไม่
  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // ฟังก์ชันตรวจสอบว่า path ปัจจุบันตรงกับ submenu หรือไม่
  const hasActiveSubmenu = (menuItem: MenuItem): boolean => {
    if (!menuItem.submenu) return false;
    return menuItem.submenu.some(subItem => isActive(subItem.path));
  };

  // FEEDBACK-SIDEBAR-COLLAPSE: นำทางเข้าหน้าในหัวข้อไหน → ขยายหัวข้อนั้นอัตโนมัติ
  // (เมนูของหน้าปัจจุบันต้องไม่ถูกซ่อน) · ถ้าผู้ใช้กดย่อเองระหว่างอยู่หน้านั้น
  // pathname ไม่เปลี่ยน effect ไม่ทำงาน → เคารพการย่อของผู้ใช้
  useEffect(() => {
    const toExpand = menuItems.filter(
      mi => mi.submenu && hasActiveSubmenu(mi) && collapsedSections[mi.path]
    );
    if (toExpand.length > 0) {
      setCollapsedSections(prev => {
        const next = { ...prev };
        toExpand.forEach(mi => { next[mi.path] = false; });
        persistCollapsedSections(next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ตรวจสอบสิทธิ์การเข้าถึงเมนู - ใช้ permission system ใหม่
  const hasMenuPermission = (permissions?: string[]): boolean => {
    // ถ้าไม่ระบุสิทธิ์หรือเป็นอาร์เรย์ว่าง ทุกคนเข้าถึงได้
    if (!permissions || permissions.length === 0) return true;

    // ถ้าเป็น Super Admin สามารถเข้าถึงได้ทุกอย่าง
    if (isSuperAdmin) return true;

    // ใช้ checkPermission จาก usePermission hook
    // ถ้ามี permission อย่างน้อย 1 อันที่ตรง ก็ให้เข้าถึงได้
    return permissions.some(p => checkPermission(p));
  };

  // BUG-S310 FIX: parent menu (มี submenu) ลิงก์ไป path ของตัวเอง แต่ path นั้นอาจ redirect
  // ไปยัง route ที่ต้องการสิทธิ์ที่ role ไม่มี (เช่น Viewer มี roles.view แต่ไม่มี users.view →
  // /users → /users/list → /access-denied) ทั้งที่ group ถูกแสดงเพราะมี child ที่เข้าถึงได้.
  // แก้: ถ้าเข้าถึง child ตัวแรก (primary) ได้ → คง path เดิม (ไม่กระทบ standalone-page parent
  // เช่น /master-data, /reports, /rentals). ถ้า primary child เข้าไม่ได้ →
  // fallback ไป child ตัวแรกที่เข้าถึงได้ กันคลิกแล้วเด้ง /access-denied.
  // G-05 invariant: submenu[0] ของ "ตั้งค่า" ต้องเป็น entry ที่ gate ด้วย settings.view เสมอ
  // (ดู config/navigation.tsx) — ไม่งั้น role ที่มี settings.view แต่ไม่มี settings.edit
  // จะโดน fallback เด้งไป child อื่นแทนที่จะไปหน้า /settings hub
  const parentMenuTarget = (menuItem: MenuItem): string => {
    if (!menuItem.submenu || menuItem.submenu.length === 0) return menuItem.path;
    const primary = menuItem.submenu[0];
    if (hasMenuPermission(primary.permissions)) return menuItem.path;
    const accessible = menuItem.submenu.find(s => hasMenuPermission(s.permissions));
    return accessible ? accessible.path : menuItem.path;
  };

  // P-S616-1: โลโก้บน sidebar เคยลิงก์ /dashboard ตายตัว → role ที่ไม่มี dashboard.view
  // กดแล้วเด้ง /access-denied ทุกครั้ง (ทางตันซ้ำรอยเดิม) → ชี้ไปเมนูแรกที่เข้าถึงได้แทน
  const brandTarget = resolveLandingRoute(hasMenuPermission);

  // P-SA59-1: ป้ายบทบาทบน header/sidebar เคยโชว์ `role_name` ดิบ → บัญชี superuser
  // (is_superuser=1 แต่ role_id ชี้ role ธรรมดา เช่น Sales) เห็น "Sales" ทั้งที่แถวเดียวกัน
  // ในหน้า users-list โชว์ "Super Admin" (pages/settings/UserPage.tsx:449) = ขัดกันเองข้ามจอ
  // และไม่สะท้อนสิทธิ์จริงที่ bypass ทุก permission. ยึดตาม users-list เป็นหลัก
  const roleLabel = (isSuperAdmin || isSuperUser)
    ? 'Super Admin'
    : (user?.role_name || (user?.role === 'admin' ? 'ผู้ดูแลระบบ' : 'ผู้ใช้งาน'));

  // สร้าง submenu component - Executive Navy Theme
  const renderSubmenu = (menuItem: MenuItem, isMobile: boolean = false) => {
    if (!menuItem.submenu) return null;

    return (
      <div className="mt-1 ml-4 pl-3 border-l border-primary-700/40 space-y-0.5">
        {menuItem.submenu.map((subItem, index) => (
          hasMenuPermission(subItem.permissions) && (
            <Link
              key={index}
              to={subItem.path}
              onClick={() => {
                if (isMobile) {
                  setIsMobileSidebarOpen(false);
                }
              }}
              aria-current={isActive(subItem.path) ? 'page' : undefined}
              className={cn(
                'flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-150',
                isActive(subItem.path)
                  ? 'bg-white/15 text-white font-medium shadow-sm'
                  : 'text-primary-300 hover:bg-primary-700/40 hover:text-white'
              )}
            >
              <span className={cn(
                'flex-shrink-0 w-4 h-4 mr-2.5',
                isActive(subItem.path) ? 'opacity-100' : 'opacity-60'
              )}>
                {subItem.icon}
              </span>
              <span className="truncate">{subItem.title}</span>
              {isActive(subItem.path) && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0" />
              )}
            </Link>
          )
        ))}
      </div>
    );
  };

  // โหมดแถบไอคอน (rail) — เหลือเฉพาะไอคอน · hover/focus แล้วขึ้นชื่อเมนู
  // เมนูที่มีเมนูย่อย: hover → flyout ด้านขวาให้กดเมนูย่อยได้โดยไม่ต้องขยาย sidebar กลับ
  const renderRailMenu = () => (
    <nav className="mt-3 px-2" aria-label="เมนูหลัก (แถบไอคอน)">
      <div className="space-y-1">
        {menuItems.map((menuItem, index) => (
          hasMenuPermission(menuItem.permissions) && (
            <div
              key={index}
              className="relative"
              onMouseEnter={(e) => menuItem.submenu && openRailFlyout(menuItem.path, e.currentTarget)}
              onMouseLeave={() => menuItem.submenu && scheduleCloseRailFlyout()}
            >
              <Link
                to={menuItem.submenu ? parentMenuTarget(menuItem) : menuItem.path}
                title={menuItem.title}
                aria-label={menuItem.title}
                aria-current={isActive(menuItem.path) ? 'page' : undefined}
                onFocus={(e) => menuItem.submenu && openRailFlyout(menuItem.path, e.currentTarget)}
                className={cn(
                  'flex items-center justify-center h-11 rounded-lg transition-colors relative',
                  (menuItem.submenu ? hasActiveSubmenu(menuItem) : isActive(menuItem.path))
                    ? 'bg-accent-500/20 text-accent-300'
                    : 'text-primary-100 hover:bg-primary-700/50 hover:text-white'
                )}
              >
                <span className="w-5 h-5 flex-shrink-0">{menuItem.icon}</span>
                {menuItem.submenu && (
                  <span className="absolute right-1 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-primary-400" />
                )}
              </Link>
            </div>
          )
        ))}
      </div>
    </nav>
  );

  // panel ที่ลอยออกมาข้างแถบไอคอน (fixed — ไม่โดน overflow ของ container เมนู clip)
  const renderRailFlyout = () => {
    if (!railFlyout) return null;
    const menuItem = menuItems.find(mi => mi.path === railFlyout.key);
    if (!menuItem || !menuItem.submenu || !hasMenuPermission(menuItem.permissions)) return null;
    return (
      <div
        className="hidden lg:block fixed left-16 z-modal ml-1 w-56 rounded-xl bg-primary-900 shadow-2xl ring-1 ring-primary-700/60 py-2"
        style={{ top: Math.max(8, Math.min(railFlyout.top, window.innerHeight - 320)) }}
        onMouseEnter={() => openRailFlyoutKeepOpen()}
        onMouseLeave={scheduleCloseRailFlyout}
      >
        <Link
          to={parentMenuTarget(menuItem)}
          className="flex items-center gap-2 px-3 pb-2 mb-1 border-b border-primary-700/50 text-sm font-semibold text-white hover:text-accent-300"
          onClick={() => setRailFlyout(null)}
        >
          <span className="w-4 h-4 flex-shrink-0">{menuItem.icon}</span>
          <span className="truncate">{menuItem.title}</span>
        </Link>
        <div className="space-y-0.5 px-1.5">
          {menuItem.submenu.map((subItem, i) => (
            hasMenuPermission(subItem.permissions) && (
              <Link
                key={i}
                to={subItem.path}
                onClick={() => setRailFlyout(null)}
                aria-current={isActive(subItem.path) ? 'page' : undefined}
                className={cn(
                  'flex items-center px-2.5 py-2 text-sm rounded-lg transition-colors',
                  isActive(subItem.path)
                    ? 'bg-white/15 text-white font-medium'
                    : 'text-primary-200 hover:bg-primary-700/50 hover:text-white'
                )}
              >
                <span className="w-4 h-4 mr-2.5 opacity-70 flex-shrink-0">{subItem.icon}</span>
                <span className="truncate">{subItem.title}</span>
              </Link>
            )
          ))}
        </div>
      </div>
    );
  };

  // ผลการค้นหา: กลุ่มที่ชื่อตรง → โชว์เมนูย่อยทั้งกลุ่ม · เมนูย่อยที่ตรง → โชว์เฉพาะรายการนั้น
  // (กรองด้วยสิทธิ์ชุดเดียวกับเมนูปกติ — ผู้ใช้จะไม่เห็นเมนูที่ตัวเองเข้าไม่ได้)
  const menuSearchResults = React.useMemo(() => {
    const q = normalizeMenuText(menuQuery.trim());
    if (!q) return null;
    const results: { group: MenuItem | null; item: MenuItem }[] = [];
    menuItems.forEach(mi => {
      if (!hasMenuPermission(mi.permissions)) return;
      const groupMatched = normalizeMenuText(mi.title).includes(q);
      if (mi.submenu && mi.submenu.length > 0) {
        mi.submenu.forEach(sub => {
          if (!hasMenuPermission(sub.permissions)) return;
          if (groupMatched || normalizeMenuText(sub.title).includes(q)) {
            results.push({ group: mi, item: sub });
          }
        });
      } else if (groupMatched) {
        results.push({ group: null, item: mi });
      }
    });
    return results;
  }, [menuQuery, user, isSuperAdmin]);

  // Ctrl/Cmd+K → โฟกัสช่องค้นหาเมนู (ถ้าย่อเป็นแถบไอคอนอยู่ ให้ขยายกลับก่อน ไม่งั้นไม่มีช่องให้พิมพ์)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (isRail) {
          setIsRail(false);
          try { localStorage.setItem(SIDEBAR_RAIL_KEY, '0'); } catch { /* private mode */ }
        }
        window.setTimeout(() => {
          const target = window.innerWidth >= 1024 ? menuSearchRef.current : mobileMenuSearchRef.current;
          target?.focus();
          target?.select();
        }, 60);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isRail]);

  // แถบค้นหา — ใช้ทั้ง sidebar และ drawer มือถือ
  const renderMenuSearch = (isMobile: boolean = false) => (
    <div className={cn('px-3 pt-3', isMobile ? 'pb-1' : 'pb-0')}>
      <div className="relative">
        <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-400 pointer-events-none" />
        <input
          ref={isMobile ? mobileMenuSearchRef : menuSearchRef}
          type="text"
          value={menuQuery}
          onChange={(e) => setMenuQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setMenuQuery(''); e.currentTarget.blur(); }
            // Enter = ไปเมนูแรกที่ค้นเจอ (พิมพ์แล้วกด Enter ได้เลย ไม่ต้องละมือไปคลิก)
            if (e.key === 'Enter' && menuSearchResults && menuSearchResults.length > 0) {
              navigate(menuSearchResults[0].item.path);
              setMenuQuery('');
              if (isMobile) setIsMobileSidebarOpen(false);
              e.currentTarget.blur();
            }
          }}
          placeholder="ค้นหาเมนู…"
          aria-label="ค้นหาเมนู"
          className="w-full bg-primary-800/60 text-sm text-white placeholder-primary-400 rounded-lg pl-9 pr-8 py-2 border border-primary-700/60 focus:outline-none focus:ring-2 focus:ring-accent-400/60 focus:border-transparent"
        />
        {menuQuery && (
          <button
            type="button"
            onClick={() => { setMenuQuery(''); (isMobile ? mobileMenuSearchRef : menuSearchRef).current?.focus(); }}
            aria-label="ล้างคำค้นหา"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-primary-300 hover:text-white hover:bg-primary-700/60"
          >
            <RiCloseLine className="w-4 h-4" />
          </button>
        )}
      </div>
      {!menuQuery && !isMobile && (
        <p className="mt-1.5 text-2xs text-primary-500 text-center">กด Ctrl+K (Mac: ⌘K) เพื่อค้นหาเมนู</p>
      )}
    </div>
  );

  // รายการผลการค้นหา — แสดงชื่อกลุ่มกำกับไว้ให้รู้ว่าเมนูนั้นอยู่ใต้หมวดไหน
  const renderMenuSearchResults = (isMobile: boolean = false) => {
    const results = menuSearchResults || [];
    if (results.length === 0) {
      return (
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-primary-300">ไม่พบเมนูที่ตรงกับ “{menuQuery}”</p>
          <p className="mt-1 text-2xs text-primary-500">ลองพิมพ์คำสั้นลง เช่น “เช่า” “ซ่อม” “ใบแจ้งหนี้”</p>
        </div>
      );
    }
    return (
      <nav className="mt-2 px-3" aria-label="ผลการค้นหาเมนู">
        <p className="px-1 pb-2 text-2xs text-primary-400">พบ {results.length} เมนู</p>
        <div className="space-y-0.5">
          {results.map(({ group, item }, i) => (
            <Link
              key={`${item.path}-${i}`}
              to={item.path}
              onClick={() => { setMenuQuery(''); if (isMobile) setIsMobileSidebarOpen(false); }}
              aria-current={isActive(item.path) ? 'page' : undefined}
              className={cn(
                'flex items-center px-3 py-2 rounded-lg transition-colors',
                isActive(item.path)
                  ? 'bg-white/15 text-white'
                  : 'text-primary-100 hover:bg-primary-700/50 hover:text-white'
              )}
            >
              <span className="flex-shrink-0 w-4 h-4 mr-2.5 opacity-70">{item.icon}</span>
              <span className="min-w-0">
                <span className="block text-sm truncate">{item.title}</span>
                {group && <span className="block text-2xs text-primary-400 truncate">{group.title}</span>}
              </span>
            </Link>
          ))}
        </div>
      </nav>
    );
  };

  // สร้าง menu component - Executive Navy Theme
  const renderMenu = (isMobile: boolean = false) => {
    return (
      <nav className="mt-4 px-3" aria-label={isMobile ? "เมนูหลัก (มือถือ)" : "เมนูหลัก"}>
        <div className="space-y-1">
          {/* แสดงเมนูตามสิทธิ์ */}
          {menuItems.map((menuItem, index) => (
            hasMenuPermission(menuItem.permissions) && (
              <div key={index} className="mb-1">
                {menuItem.submenu ? (
                  <div>
                    {/* FEEDBACK-SIDEBAR-COLLAPSE: แถวหัวข้อ = ลิงก์ (นำทางเหมือนเดิม)
                        + ปุ่ม chevron ย่อ/ขยาย submenu ของหัวข้อนั้น */}
                    <div
                      className={cn(
                        'flex items-center rounded-lg transition-colors',
                        hasActiveSubmenu(menuItem)
                          ? 'bg-accent-500/20'
                          : 'hover:bg-primary-700/50'
                      )}
                    >
                      <Link
                        to={parentMenuTarget(menuItem)}
                        className={cn(
                          'flex items-center flex-1 min-w-0 pl-3 pr-1 py-2.5 text-sm font-medium rounded-l-lg cursor-pointer transition-colors',
                          hasActiveSubmenu(menuItem)
                            ? 'text-accent-300'
                            : 'text-primary-100 hover:text-white'
                        )}
                        onClick={() => {
                          if (isMobile) {
                            setIsMobileSidebarOpen(false);
                          }
                        }}
                      >
                        <span className="flex-shrink-0 w-5 h-5 mr-3">{menuItem.icon}</span>
                        <span className="truncate">{menuItem.title}</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => toggleSection(menuItem.path)}
                        aria-expanded={!collapsedSections[menuItem.path]}
                        aria-label={collapsedSections[menuItem.path]
                          ? `ขยายเมนู ${menuItem.title}`
                          : `ย่อเมนู ${menuItem.title}`}
                        className={cn(
                          'flex-shrink-0 mr-1 min-w-[32px] min-h-[36px] flex items-center justify-center rounded-md transition-colors',
                          hasActiveSubmenu(menuItem)
                            ? 'text-accent-300 hover:bg-accent-500/20'
                            : 'text-primary-300 hover:bg-primary-600/50 hover:text-white'
                        )}
                      >
                        <RiArrowDownSLine
                          className={cn(
                            'w-4 h-4 transition-transform duration-200',
                            collapsedSections[menuItem.path] && '-rotate-90'
                          )}
                        />
                      </button>
                    </div>
                    {!collapsedSections[menuItem.path] && renderSubmenu(menuItem, isMobile)}
                  </div>
                ) : (
                  <Link
                    to={menuItem.path}
                    onClick={() => {
                      if (isMobile) {
                        setIsMobileSidebarOpen(false);
                      }
                    }}
                    aria-current={isActive(menuItem.path) ? 'page' : undefined}
                    className={cn(
                      'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                      isActive(menuItem.path)
                        ? 'bg-accent-500/20 text-accent-300'
                        : 'text-primary-100 hover:bg-primary-700/50 hover:text-white'
                    )}
                  >
                    <span className="flex-shrink-0 w-5 h-5 mr-3">{menuItem.icon}</span>
                    <span>{menuItem.title}</span>
                  </Link>
                )}
              </div>
            )
          ))}
        </div>
      </nav>
    );
  };

  return (
    <div className="flex h-screen bg-secondary-50">
      {/* A11y: Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:rounded focus:shadow-lg focus:text-primary-700 focus:font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        ข้ามไปยังเนื้อหาหลัก
      </a>
      {/* Sidebar สำหรับหน้าจอกว้าง - Executive Navy Theme */}
      {/* FINDING-038 FIX (S405): use <aside> landmark to contain sidebar content (logo + user + nav) */}
      <aside
        aria-label="แถบนำทางหลัก"
        className={cn(
          'hidden lg:flex fixed inset-y-0 left-0 z-docked bg-gradient-to-b from-primary-800 via-primary-900 to-primary-950 shadow-sidebar transform flex-col',
          'transition-all duration-200 ease-out lg:translate-x-0 lg:static',
          isRail ? 'w-16 lg:w-16' : 'w-64 lg:w-64',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo Header */}
        <div className={cn(
          'flex items-center h-14 border-b border-primary-700/50',
          isRail ? 'justify-center px-2' : 'px-4'
        )}>
          {!isRail && (
            <Link to={brandTarget} className="flex items-center gap-2.5 group flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors flex-shrink-0">
                <RiBuildingLine className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <span className="block text-base font-semibold text-white tracking-tight leading-tight truncate">KRP System</span>
                <span className="block text-2xs text-primary-400 leading-tight">Equipment Rental</span>
              </div>
            </Link>
          )}
          {/* FEEDBACK-SIDEBAR-RAIL: ปุ่มย่อ/ขยายแถบเมนู (เห็นเฉพาะจอ lg ขึ้นไป — จอเล็กใช้ปุ่ม ≡ ที่ header อยู่แล้ว) */}
          <button
            type="button"
            onClick={toggleRail}
            title={isRail ? 'ขยายแถบเมนู' : 'ย่อแถบเมนูเหลือไอคอน'}
            aria-label={isRail ? 'ขยายแถบเมนู' : 'ย่อแถบเมนูเหลือไอคอน'}
            aria-pressed={isRail}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-primary-300 hover:text-white hover:bg-primary-700/60 transition-colors"
          >
            {isRail ? <RiMenuUnfoldLine className="w-5 h-5" /> : <RiMenuFoldLine className="w-5 h-5" />}
          </button>
        </div>

        {/* User Profile Section */}
        <div className={cn('border-b border-primary-700/40', isRail ? 'px-2 py-3' : 'px-3 py-3')}>
          <div className={cn(
            'flex items-center bg-primary-800/40 rounded-lg',
            isRail ? 'justify-center py-2' : 'gap-2.5 px-3 py-2.5'
          )}>
            <div
              className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center text-white font-semibold text-sm ring-2 ring-accent-300/20 flex-shrink-0"
              title={isRail ? `${user?.name || 'ผู้ใช้งาน'} · ${roleLabel}` : undefined}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            {!isRail && (
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate leading-tight">{user?.name || 'ผู้ใช้งาน'}</p>
                <p className="text-2xs text-primary-400 truncate leading-tight">{roleLabel}</p>
              </div>
            )}
          </div>
        </div>

        {/* ค้นหาเมนู — ซ่อนตอนย่อเป็นแถบไอคอน (Ctrl+K จะขยายกลับให้เอง) */}
        {!isRail && renderMenuSearch()}

        {/* เมนู */}
        <div ref={sidebarScrollRef} className="flex-1 overflow-y-auto">
          {isRail
            ? renderRailMenu()
            : (menuSearchResults ? renderMenuSearchResults() : renderMenu())}
        </div>

        {/* ปุ่มออกจากระบบ */}
        <div className={cn(
          'flex-shrink-0 w-full border-t border-primary-700/50 bg-primary-900/95 backdrop-blur-sm',
          isRail ? 'p-2' : 'p-4'
        )}>
          <button
            onClick={logout}
            title={isRail ? 'ออกจากระบบ' : undefined}
            aria-label="ออกจากระบบ"
            className={cn(
              'flex items-center w-full text-sm font-medium text-primary-200 rounded-lg hover:bg-danger-600/20 hover:text-danger-300 transition-colors',
              isRail ? 'justify-center py-2.5' : 'px-3 py-2.5'
            )}
          >
            <RiLogoutBoxLine className="w-5 h-5" />
            {!isRail && <span className="ml-3">ออกจากระบบ</span>}
          </button>
          {/* App version — for support/troubleshooting */}
          {!isRail && (
            <p className="mt-3 text-center text-2xs text-primary-500 select-text" title={VERSION_LABEL}>
              {VERSION_LABEL}
            </p>
          )}
        </div>
      </aside>
      {renderRailFlyout()}

      {/* เนื้อหาหลัก */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white z-docked relative border-b border-secondary-200/80" style={{ boxShadow: '0 1px 3px 0 rgba(15, 39, 68, 0.04), 0 1px 2px -1px rgba(15, 39, 68, 0.04)' }}>
          <div className="flex items-center h-14 px-4 gap-3">
            <button
              onClick={() => {
                setIsMobileSidebarOpen(!isMobileSidebarOpen);
                setIsUserDropdownOpen(false); // ปิด dropdown เมื่อเปิด sidebar
              }}
              className="p-2.5 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 transition-colors lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="เปิดเมนู"
            >
              <RiMenuLine className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1.5 sm:gap-2 ml-auto">
              {/* ปุ่มศูนย์ช่วยเหลือ / คู่มือการใช้งาน */}
              <Link
                to="/help"
                title="คู่มือการใช้งาน"
                aria-label="คู่มือการใช้งาน"
                className="p-2 rounded-lg text-secondary-500 hover:text-primary-600 hover:bg-secondary-100 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center"
              >
                <RiQuestionLine className="w-5 h-5" />
              </Link>
              <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onClearAll={clearNotifications}
                onRemove={removeNotification}
                settingsHref={checkPermission('notifications.view') ? '/settings/notifications' : undefined}
                canViewAllNotifications={checkPermission('notifications.view')}
              />
              {/* Divider */}
              <div className="w-px h-5 bg-secondary-200 mx-0.5 hidden sm:block" />
              {/* User dropdown */}
              <div className="relative" ref={userDropdownRef}>
                <button
                  ref={userButtonRef}
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  aria-haspopup="true"
                  aria-expanded={isUserDropdownOpen}
                  aria-label="เมนูผู้ใช้งาน"
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5',
                    'rounded-lg',
                    'text-sm font-medium text-secondary-700',
                    'hover:bg-secondary-100',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
                    'transition-colors'
                  )}
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-xs font-semibold text-secondary-800 leading-tight">{user?.name || 'ผู้ใช้งาน'}</p>
                    <p className="text-2xs text-secondary-600 leading-tight">{roleLabel}</p>
                  </div>
                  <svg className="w-3.5 h-3.5 text-secondary-400 hidden sm:block" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-dropdown border border-secondary-200 z-dropdown overflow-hidden animate-fade-in">
                    <div className="py-1">
                      <div className="px-4 py-3 border-b border-secondary-100 bg-gradient-to-r from-primary-50/50 to-white">
                        <p className="text-sm font-semibold text-secondary-900">{user?.name || 'ผู้ใช้งาน'}</p>
                        <p className="text-xs text-secondary-400 mt-0.5">{user?.email || ''}</p>
                        {(isSuperAdmin || isSuperUser || user?.role_name || user?.role) && (
                          <span className="inline-block mt-1.5 px-2 py-0.5 text-2xs font-medium rounded-full bg-primary-100 text-primary-700">
                            {roleLabel}
                          </span>
                        )}
                      </div>
                      <div className="py-1">
                        <Link
                          to="/profile"
                          onClick={() => setIsUserDropdownOpen(false)}
                          className="flex items-center w-full px-4 min-h-[44px] sm:min-h-[42px] py-2.5 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors gap-2.5"
                        >
                          <RiUserSettingsLine className="w-4 h-4 text-secondary-400" />
                          โปรไฟล์ของฉัน
                        </Link>
                        {checkPermission('settings.view') && (
                          <Link
                            to="/settings"
                            onClick={() => setIsUserDropdownOpen(false)}
                            className="flex items-center w-full px-4 min-h-[44px] sm:min-h-[42px] py-2.5 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors gap-2.5"
                          >
                            <RiSettingsLine className="w-4 h-4 text-secondary-400" />
                            ตั้งค่าระบบ
                          </Link>
                        )}
                      </div>
                      <div className="border-t border-secondary-100 py-1">
                        <button
                          onClick={() => {
                            setIsUserDropdownOpen(false);
                            logout();
                          }}
                          className="flex items-center w-full px-4 min-h-[44px] sm:min-h-[42px] py-2.5 text-sm text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors gap-2.5"
                        >
                          <RiLogoutBoxLine className="w-4 h-4" />
                          ออกจากระบบ
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        
        {/* Mobile sidebar - Executive Navy Theme */}
        {isMobileSidebarOpen && (
          <div className="fixed inset-0 z-overlay lg:hidden">
            <div
              className="absolute inset-0 bg-primary-950/60 backdrop-blur-sm animate-fade-in"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
            <aside
              aria-label="แถบนำทางหลัก (มือถือ)"
              className="fixed inset-y-0 left-0 z-modal w-full max-w-xs bg-gradient-to-b from-primary-800 via-primary-900 to-primary-950 flex flex-col shadow-modal animate-slide-right"
              onClick={(e) => e.stopPropagation()}
            >{/* FINDING-038 PATTERN (S406) — mobile sidebar landmark */}
              <div className="flex items-center justify-between h-16 px-4 border-b border-primary-700/50">
                <Link to={brandTarget} className="flex items-center group">
                  <div className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <RiBuildingLine className="w-6 h-6 text-white" />
                  </div>
                  <span className="ml-2.5 text-lg font-semibold text-white tracking-tight">KRP System</span>
                </Link>
                <button
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className="p-1.5 rounded-lg text-primary-300 hover:text-white hover:bg-primary-700/50 transition-colors"
                  aria-label="ปิดเมนู"
                >
                  <RiCloseLine className="w-6 h-6" />
                </button>
              </div>

              <div className="p-4 border-b border-primary-700/50 bg-primary-800/30">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center text-white font-semibold ring-2 ring-accent-300/30 shadow-lg">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-white">{user?.name || 'ผู้ใช้งาน'}</p>
                    <p className="text-xs text-primary-300">{roleLabel}</p>
                  </div>
                </div>
              </div>

              {/* เมนูบนมือถือ */}
              {renderMenuSearch(true)}
              <div ref={mobileSidebarScrollRef} className="flex-1 overflow-y-auto">
                {menuSearchResults ? renderMenuSearchResults(true) : renderMenu(true)}
              </div>

              {/* ปุ่มออกจากระบบบนมือถือ */}
              <div className="flex-shrink-0 w-full p-4 border-t border-primary-700/50 bg-primary-900/95 backdrop-blur-sm">
                <button
                  onClick={logout}
                  className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-primary-200 rounded-lg hover:bg-danger-600/20 hover:text-danger-300 transition-colors"
                >
                  <RiLogoutBoxLine className="w-5 h-5" />
                  <span className="ml-3">ออกจากระบบ</span>
                </button>
              </div>
            </aside>
          </div>
        )}
        
        {/* พื้นที่แสดงเนื้อหา */}
        <main id="main-content" className="flex-1 overflow-auto bg-secondary-50/80" tabIndex={-1}>
          <div className="px-4 sm:px-6 py-5 sm:py-6">
            <OfflineBanner />
            {children}
          </div>
        </main>

        {/* Floating Help Button — เปิดศูนย์ช่วยเหลือในแอป
            BUG-S141-FABSTACK: เดิมอยู่ที่ bottom-6 right-6 z-50 ตำแหน่ง/ขนาด/สี
            เดียวกับปุ่ม "แจ้งปัญหา / ส่ง Feedback" (FeedbackWidget z-[60]) เป๊ะ →
            ปุ่ม Feedback ทับสนิททุกพิกเซล คลิกปุ่มคู่มือไม่โดนเลย (elementFromPoint
            กลางปุ่มคืน "แจ้งปัญหา / ส่ง Feedback") · ย้ายขึ้นไปวางเหนือปุ่ม Feedback
            · 2026-08-31: z-50 → z-popover ให้เท่ากับปุ่มแจ้งปัญหา — ปัญหา class เดียวกัน
            (โดน backdrop z-modal ของ ui/Modal บังตอน modal เปิด) */}
        <Link
          to="/help"
          title="คู่มือการใช้งาน"
          aria-label="คู่มือการใช้งาน"
          className="fab-help fixed bottom-24 right-6 z-popover w-12 h-12 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
        >
          <RiQuestionLine className="w-6 h-6" />
        </Link>
      </div>
    </div>
  );
};

export default Layout;